'use no memo'; // reads the controller's live geometry at render: `busy` flips after every camera move

import { useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import type { SceneStore } from '@os/doc';
import { Captions, Crop, FileSpreadsheet, Files, ImageUp, SlidersHorizontal, SquareArrowOutUpRight } from 'lucide-react';
import type { EditorController } from './controller';
import { CANVAS_UI, FLOATING_BAR } from './canvas-ui';
import { authorIds, authorsOf, type AuthorsSource } from './authors';
import { useAuthorNames } from './useAuthors';
import { useSelectionProps, type SelectionProps } from './useSelectionProps';
import { ActionButtons, ColorField, hasColor, HomeLine, OrderField, TypeFields } from './properties-fields';
import { placeContextBar, ROTATE_RESERVE_PX, SECTION_RESERVE_PX, type FreeArea } from './context-bar';
import { handlesFor } from '@os/engine';
import { Popover } from '../../ui/Popover';
import { Icon } from '../../ui/icons';
import { cn, IconButton, Input } from '../../ui';
import { iconTriggerClass } from '../../ui/focus';

const MARGIN = 8;
const keyboardOnly = (type: string) => type === 'keyboard';
const trigger = cn(iconTriggerClass, 'h-9 gap-1.5 px-2.5 hover:bg-chrome-hover pointer-coarse:h-11 pointer-coarse:min-w-11');

/** The viewport's free area (viewport pixels): the view minus the top bar and the toolbar, with a margin. */
function freeArea(origin: DOMRect | null, view: { w: number; h: number }): FreeArea {
  const ox = origin?.left ?? 0;
  const oy = origin?.top ?? 0;
  const area = { top: MARGIN, left: MARGIN, right: view.w - MARGIN, bottom: view.h - MARGIN };
  const top = document.querySelector('[data-topbar]')?.getBoundingClientRect();
  if (top && top.height > 0) area.top = Math.max(area.top, top.bottom - oy + MARGIN);
  const bar = document.querySelector<HTMLElement>('[role="toolbar"][data-layout]');
  const r = bar?.getBoundingClientRect();
  if (bar && r && r.height > 0) {
    if (bar.dataset.layout === 'tablet') area.left = Math.max(area.left, r.right - ox + MARGIN);
    else area.bottom = Math.min(area.bottom, r.top - oy - MARGIN);
  }
  return area;
}

/**
 * The selection's context bar (P5f, backlog §14.1), floating above it on desktop and tablet: who added it, its colour,
 * its options (font size, section and connector fields, order), then lock, duplicate, arrange and delete. It follows
 * the selection, keeps its size at every zoom, flips below when there is no room above, never leaves the free area,
 * and hides during a gesture, a camera move and while a creation tool is armed; while the object's text is edited it
 * moves below (the format bar is above). Readers see the author only. Edits go through store commands only.
 */
export function ContextBar({
  controller,
  store,
  authors,
  editing,
  onReplaceImage,
  onOpenPageLink,
  onRetargetPageLink,
  onExportCsv,
}: {
  controller: EditorController;
  store: SceneStore;
  /** Server boards: who added the selection (P5d). */
  authors?: AuthorsSource | null;
  /** A text, a section title or a connector label is being edited: its format bar takes the place above. */
  editing?: boolean;
  /** "Replace image": the app picks a file and swaps the picture (P5f). */
  onReplaceImage?: (id: string) => void;
  /** A selected page link (P14): « Ouvrir la page » for everyone, « Choisir une page » for editors. */
  onOpenPageLink?: (id: string) => void;
  onRetargetPageLink?: (id: string) => void;
  /** A selected table, with `can_export` (P11b): download it as CSV. */
  onExportCsv?: (id: string) => void;
}) {
  const { t } = useTranslation();
  const p = useSelectionProps(controller, store);
  const ui = useSyncExternalStore(controller.subscribeUi, controller.ui);
  const busy = useSyncExternalStore(controller.subscribeBusy, controller.busy);
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const next = { w: el.offsetWidth, h: el.offsetHeight };
    if (!size || size.w !== next.w || size.h !== next.h) setSize(next);
  });
  const readOnly = store.readOnly;
  if (!p || busy || (ui.tool !== 'select' && ui.tool !== 'hand')) return null;
  const one = p.objects.length === 1 ? p.objects[0]! : null;
  const pageLink = one?.type === 'pagelink' ? one : null;
  if (readOnly && !authors && !pageLink) return null;
  const sel = controller.selectionScreenRect();
  if (!sel) return null;
  // Clear of a section's title pill and grip, and of a single object's rotate handle.
  const reserve = p.objects.some((o) => o.type === 'section')
    ? SECTION_RESERVE_PX
    : one && !readOnly && !p.allLocked && handlesFor(one.type).includes('rotate')
      ? ROTATE_RESERVE_PX
      : 0;
  const origin = ref.current?.offsetParent?.getBoundingClientRect() ?? null;
  const at = placeContextBar(sel, size ?? { w: 0, h: 0 }, freeArea(origin, controller.viewSize()), reserve, editing ? 'below' : 'above');
  if (!at) return null;
  return (
    <div
      ref={ref}
      role="toolbar"
      aria-label={t('contextBar.label')}
      data-context-bar=""
      data-side={at.side}
      {...{ [CANVAS_UI]: '' }}
      // A click on the bar's buttons keeps the canvas focus (fields and selects still take the focus).
      onMouseDown={(e) => {
        if (!(e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement || e.target instanceof HTMLTextAreaElement)) e.preventDefault();
      }}
      style={{ left: `${Math.round(at.x)}px`, top: `${Math.round(at.y)}px`, visibility: size ? undefined : 'hidden' }}
      className={`os-enter-fade absolute z-[11] flex max-w-[calc(100vw-1rem)] items-center gap-0.5 whitespace-nowrap rounded-full p-1 font-sans text-sm text-fg ${FLOATING_BAR}`}
    >
      {authors && <AuthorChip source={authors} p={p} />}
      {pageLink && onOpenPageLink && store.pages().some((pg) => pg.id === pageLink.targetPageId) && (
        <IconButton label={t('pages.open')} icon={<Icon icon={SquareArrowOutUpRight} size={16} />} onClick={() => onOpenPageLink(pageLink.id)} />
      )}
      {pageLink && !readOnly && onRetargetPageLink && (
        <IconButton label={t('pages.retarget')} icon={<Icon icon={Files} size={16} />} disabled={p.allLocked} onClick={() => onRetargetPageLink(pageLink.id)} />
      )}
      {one?.type === 'table' && onExportCsv && <IconButton label={t('menu.exportCsv')} icon={<Icon icon={FileSpreadsheet} size={16} />} onClick={() => onExportCsv(one.id)} />}
      {!readOnly && <Edits p={p} store={store} controller={controller} divided={!!authors || !!pageLink || (one?.type === 'table' && !!onExportCsv)} onReplaceImage={onReplaceImage} />}
    </div>
  );
}

function Divider() {
  return <span role="separator" aria-orientation="vertical" className="mx-0.5 h-5 w-px shrink-0 bg-border" />;
}

/** Colour, options, then the action buttons. */
function Edits({
  p,
  store,
  controller,
  divided,
  onReplaceImage,
}: {
  p: SelectionProps;
  store: SceneStore;
  controller: EditorController;
  divided: boolean;
  onReplaceImage?: (id: string) => void;
}) {
  const { t } = useTranslation();
  const swatch = typeof p.current === 'string' ? p.current : undefined;
  const image = p.images.length === 1 ? p.images[0]! : null;
  return (
    <>
      {divided && <Divider />}
      {image && <ImageActions image={image} locked={p.allLocked} store={store} controller={controller} onReplace={onReplaceImage} />}
      {hasColor(p) && (
        <Popover.Root>
          <Popover.Trigger aria-label={t('panel.color')} className={trigger}>
            <span aria-hidden="true" data-swatch="" className="size-5 rounded-full ring-1 ring-fg/15" style={{ background: swatch ?? 'transparent' }} />
          </Popover.Trigger>
          {/* Pointer users keep the text editor focused: colouring a note while typing in it (P5b). */}
          <Popover.Popup side="top" className="w-60" label={t('panel.color')} initialFocus={keyboardOnly} finalFocus={keyboardOnly}>
            <div onMouseDown={(e) => e.preventDefault()}>
              <ColorField p={p} store={store} />
            </div>
          </Popover.Popup>
        </Popover.Root>
      )}
      <Popover.Root>
        <Popover.Trigger aria-label={t('contextBar.options')} className={trigger}>
          <Icon icon={SlidersHorizontal} size={16} />
        </Popover.Trigger>
        <Popover.Popup side="top" className="flex w-60 flex-col gap-3" label={t('contextBar.options')}>
          <div role="group" aria-label={t('contextBar.options')} className="flex flex-col gap-3">
            <HomeLine p={p} />
            <TypeFields p={p} store={store} />
            <OrderField p={p} store={store} />
          </div>
        </Popover.Popup>
      </Popover.Root>
      <Divider />
      <ActionButtons p={p} store={store} controller={controller} className="flex items-center gap-0.5" />
    </>
  );
}

type Image = SelectionProps['images'][number];

/** Replace, crop and alternative text of a single image (P5f, backlog §14.1). */
function ImageActions({ image, locked, store, controller, onReplace }: { image: Image; locked: boolean; store: SceneStore; controller: EditorController; onReplace?: (id: string) => void }) {
  const { t } = useTranslation();
  return (
    <>
      {onReplace && <IconButton label={t('contextBar.replaceImage')} icon={<Icon icon={ImageUp} size={16} />} disabled={locked} onClick={() => onReplace(image.id)} />}
      <IconButton label={t('menu.crop')} icon={<Icon icon={Crop} size={16} />} disabled={locked || image.rotation % 360 !== 0} onClick={() => controller.startCrop(image.id)} />
      <Popover.Root>
        <Popover.Trigger aria-label={t('contextBar.altText')} disabled={locked} className={cn(trigger, 'disabled:pointer-events-none disabled:opacity-40')}>
          <Icon icon={Captions} size={16} />
        </Popover.Trigger>
        <Popover.Popup side="top" className="w-72" title={t('contextBar.altText')}>
          <AltTextField key={`${image.id}:${image.alt}`} image={image} store={store} />
        </Popover.Popup>
      </Popover.Root>
      <Divider />
    </>
  );
}

/** Saved on Enter or when it loses the focus; Escape leaves it unchanged (the popover closes). */
function AltTextField({ image, store }: { image: Image; store: SceneStore }) {
  const { t } = useTranslation();
  const save = (value: string) => {
    const next = value.trim().slice(0, 500);
    if (next !== image.alt) store.commands.setProps([image.id], { alt: next });
  };
  return (
    <label className="flex flex-col gap-1.5 text-xs text-fg-muted">
      {t('contextBar.altTextHint')}
      <Input
        type="text"
        aria-label={t('contextBar.altText')}
        defaultValue={image.alt}
        maxLength={500}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save(e.currentTarget.value);
        }}
        onBlur={(e) => save(e.currentTarget.value)}
        className="h-8 px-2"
      />
    </label>
  );
}

/** The author's colour and name, "You", "3 authors"…; nothing while the names load. */
function AuthorChip({ source, p }: { source: AuthorsSource; p: SelectionProps }) {
  const { t, i18n } = useTranslation();
  const names = useAuthorNames(source, authorIds(p.objects));
  const view = authorsOf(p.objects, { me: source.me, names, t, lng: i18n.language });
  if (!view) return null;
  return (
    <span data-author-chip="" title={view.when ?? undefined} className={cn('flex max-w-48 items-center gap-1.5 px-2 text-xs text-fg-muted')}>
      <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-fg-subtle" style={view.color ? { backgroundColor: view.color } : undefined} />
      <span className="truncate">{view.label}</span>
    </span>
  );
}
